// PM5 Force Curve (0x003D) listener — attaches to an existing PM5Connection
// WITHOUT modifying pm5Connection.js. Emits 'crewz-force-curve' {t, points (N)} per drive.
// Packet: [hdr: MS nibble = total packets, LS nibble = # uint16 points][seq][LE uint16 points…] (lbs)
import { PM5_CHARACTERISTICS } from './pm5Connection';
import { startNotificationsSafe } from './bleNotify';

const forceStatus = (state, msg) => {
  window.__crewzForceStatus = { state, msg };
  window.dispatchEvent(new CustomEvent('crewz-force-status', { detail: { state, msg } }));
};

const LBS_TO_N = 4.44822;

export function parseForcePacket(dv) {
  const hdr = dv.getUint8(0);
  const total = hdr >> 4;
  const n = hdr & 0x0f;
  const points = [];
  for (let i = 0; i < n; i++) {
    const off = 2 + i * 2;
    if (off + 1 >= dv.byteLength) break;
    points.push(dv.getUint16(off, true));
  }
  return { total, seq: dv.getUint8(1), points };
}

export function createForceAssembler(onCurve) {
  let buf = [];
  let expected = 0;
  let got = 0;
  let lastSeq = -1;
  return (dv) => {
    if (!dv || dv.byteLength < 2) return;
    const { total, seq, points } = parseForcePacket(dv);
    // Dropped packet or header change mid-curve → restart with this packet
    if (got > 0 && (seq !== ((lastSeq + 1) & 0xff) || Math.max(1, total) !== expected)) got = 0;
    lastSeq = seq;
    if (got === 0) { buf = []; expected = Math.max(1, total); }
    buf.push(...points);
    got += 1;
    if (got >= expected) {
      if (buf.length >= 3) onCurve(buf.map((v) => Math.round(v * LBS_TO_N)));
      got = 0;
    }
  };
}

const MUX_UUID = PM5_CHARACTERISTICS.MULTIPLEXED_INFO.toLowerCase();
const FORCE_MUX_ID = 0x3d;

// Every service + characteristic the PM5 exposes (for the Settings diagnostics card).
export async function listPm5Gatt(conn) {
  const server = conn?.server || conn?.device?.gatt;
  if (!server?.getPrimaryServices) return null;
  const out = [];
  try {
    const services = await server.getPrimaryServices();
    for (const svc of services) {
      let chars = [];
      try {
        chars = (await svc.getCharacteristics()).map((c) => ({
          uuid: c.uuid,
          notify: !!c.properties?.notify,
          read: !!c.properties?.read,
          write: !!(c.properties?.write || c.properties?.writeWithoutResponse),
        }));
      } catch (e) { chars = [{ error: String(e?.message || e) }]; }
      out.push({ service: svc.uuid, characteristics: chars });
    }
  } catch (e) {
    out.push({ error: String(e?.message || e) });
  }
  const gatt = { at: new Date().toISOString(), device: conn?.device?.name || 'PM5', services: out };
  window.__crewzPm5Gatt = gatt;
  window.dispatchEvent(new CustomEvent('crewz-pm5-gatt', { detail: gatt }));
  return gatt;
}

// The multiplexed characteristic (0x0080) lives in the PM5 Control service
// (0x0020), NOT the rowing service — so we scan every primary service rather
// than only the rowing one (the old rowing-only lookup never found it).
async function findMuxChar(conn) {
  const uuid = MUX_UUID;
  const direct = conn?.characteristics?.[uuid];
  if (direct) return direct;
  const server = conn?.server || conn?.device?.gatt;
  if (!server?.getPrimaryServices) return null;
  try {
    const services = await server.getPrimaryServices();
    for (const svc of services) {
      try {
        const c = await svc.getCharacteristic(uuid);
        if (c) { if (conn.characteristics) conn.characteristics[uuid] = c; return c; }
      } catch { /* not in this service */ }
    }
  } catch { /* discovery failed */ }
  return null;
}

async function subscribe(char, handler, label) {
  char.addEventListener('characteristicvaluechanged', handler);
  try {
    await startNotificationsSafe(char);
    forceStatus('listening', label);
    return () => char.removeEventListener('characteristicvaluechanged', handler);
  } catch (e) {
    console.warn('PM5: force curve notifications unavailable', e?.message);
    forceStatus('error', `Force curve notifications failed: ${e?.message || e}`);
    char.removeEventListener('characteristicvaluechanged', handler);
    return null;
  }
}

export async function attachForceCurve(conn) {
  if (!conn) return () => {};
  await listPm5Gatt(conn);
  const assemble = createForceAssembler((points) => {
    window.dispatchEvent(new CustomEvent('crewz-force-curve', { detail: { t: Date.now(), points } }));
  });

  // Force curve on THIS PM5 is multiplexed on CE060080 ONLY (it has no
  // CE06003D). We subscribe to 0080 and pick out force-curve rows by their
  // data ID (0x3D). We do NOT subscribe to 003D and do NOT fall back to any
  // other characteristic — if nothing arrives we report and stop.
  const mux = await findMuxChar(conn);
  if (!mux) {
    forceStatus('unavailable', 'PM5 force curve unavailable: multiplexed CE060080 not found on this PM5 (see PM5 services in Settings). Not trying any other characteristic.');
    return () => {};
  }

  let sawForce = false;
  const handler = (event) => {
    const dv = event.target.value;
    if (!dv || dv.byteLength < 1) return;
    const id = dv.getUint8(0);
    // Raw, uncurated 0080 packet for the diagnostic recorder (every packet).
    let hex = '';
    for (let i = 0; i < dv.byteLength; i += 1) hex += dv.getUint8(i).toString(16).padStart(2, '0');
    window.dispatchEvent(new CustomEvent('crewz-mux-packet', { detail: { t: Date.now(), id, hex } }));
    // Force-curve rows carry data ID 0x3D; strip it and parse the body.
    if (id === FORCE_MUX_ID && dv.byteLength >= 3) {
      sawForce = true;
      const body = new DataView(dv.buffer, dv.byteOffset + 1, dv.byteLength - 1);
      try {
        window.dispatchEvent(new CustomEvent('crewz-force-packet', { detail: { t: Date.now(), ...parseForcePacket(body) } }));
        assemble(body);
      } catch (e) { console.warn('PM5 force curve parse error', e); }
    }
  };

  const off = await subscribe(mux, handler, 'CE060080 (multiplexed)');
  if (!off) return () => {};
  forceStatus('listening-mux', 'Listening for force curve on multiplexed CE060080 (data ID 0x3D) — row a few strokes…');

  // Watchdog: if no force-curve (0x3D) packets ever arrive on 0080, report and
  // stop — do NOT try another method.
  const watchdog = setTimeout(() => {
    if (!sawForce) {
      forceStatus('no-force-packets', 'No force-curve packets (data ID 0x3D) received on CE060080. Stopped — not trying any other characteristic.');
      console.warn('PM5 force curve: no 0x3D packets on CE060080 within watchdog window');
    }
  }, 30000);

  return () => { clearTimeout(watchdog); off(); };
}
